import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { fetchInbox, fetchKassaDay } from '../../adminApi'
import { inboxWaiting } from '../../attention/inboxWaiting'
import { useCan } from '../../useCan'
import { today } from '../../../core/format/dates'
import type { AdminSectionKey } from '../../../data/adminSections'

/** Niet vaker dan dit opnieuw tellen bij doorklikken. */
const REFRESH_MS = 30_000

export type NavCounts = Partial<Record<AdminSectionKey, number>>

/**
 * Hoeveel er wacht, voor een getal in het menu: Inbox (inpakken, afspraak bevestigen, kuur koppelen)
 * en Kassa (afspraken van vandaag zonder bon). Telt bij openen en bij doorklikken, hoogstens elke
 * 30 seconden. Een fout laat het getal weg: het menu werkt gewoon door.
 */
export function useNavCounts(): NavCounts {
  const { pathname } = useLocation()
  // De inbox is van Sarah: een collega haalt hem niet op (alleen voor een getal in een menu dat ze niet ziet).
  const { canAll, can } = useCan()
  const seesInbox = canAll('orders.view')
  const seesKassa = can('kassa.sell')
  const [counts, setCounts] = useState<NavCounts>({})
  const last = useRef(0)

  useEffect(() => {
    if (Date.now() - last.current < REFRESH_MS) return
    last.current = Date.now()
    const controller = new AbortController()
    const opts = { signal: controller.signal }
    let settled = false
    const inboxLoad = seesInbox ? fetchInbox(opts) : Promise.reject(new Error('geen inbox'))
    const kassaLoad = seesKassa ? fetchKassaDay(today(), opts) : Promise.reject(new Error('geen kassa'))
    void Promise.allSettled([inboxLoad, kassaLoad]).then(([inbox, kassa]) => {
      settled = true
      if (controller.signal.aborted) return
      const next: NavCounts = {}
      if (inbox.status === 'fulfilled') next.inbox = inboxWaiting(inbox.value).total
      if (kassa.status === 'fulfilled') next.kassa = kassa.value.toSettle.length
      setCounts(next)
    })
    return () => {
      if (settled) return
      controller.abort()
      // Afgebroken voor het klaar was (snel doorgeklikt): de volgende keer meteen opnieuw.
      last.current = 0
    }
  }, [pathname, seesInbox, seesKassa])

  return counts
}
